import type {
  IActiveCampaign,
  ICampaignOperationSequenceItem,
  campaignTimeTrackerItem,
} from 'src/common/interfaces/campaigns.interfaces';

export type TCampaignTimeTrackerOperationRow = {
  estimate: campaignTimeTrackerItem;
  estimateIdx: number;
  operation: any;
  operationIdx: number;
};

const getOperationSequenceKey = (
  item: Pick<ICampaignOperationSequenceItem, 'campaignsEstimatesId' | 'projectSectionId'>,
) => `${item.campaignsEstimatesId}:${item.projectSectionId}`;

const getStartTime = (value?: string | Date) => {
  const time = new Date(value ?? 0).getTime();
  return Number.isNaN(time) ? 0 : time;
};

export const getCampaignTimeTrackerOperationRows = (
  timeTracker: campaignTimeTrackerItem[] = [],
  campaignId?: string,
): TCampaignTimeTrackerOperationRow[] =>
  timeTracker
    .filter((estimate) => !campaignId || estimate.campaignId === campaignId)
    .flatMap((estimate, estimateIdx: number) =>
      estimate.operations.map((operation, operationIdx) => ({
        estimate,
        estimateIdx,
        operation,
        operationIdx,
      })),
    )
    .sort((a, b) => getStartTime(a.operation.start) - getStartTime(b.operation.start));

const getExpectedTimeTrackerSequenceKeys = (activeCampaign?: IActiveCampaign) => {
  const estimates = activeCampaign?.estimates ?? [];
  const estimateById = new Map(estimates.map((estimate) => [estimate.campaignsEstimatesId, estimate]));
  const trackableOperationIdsByEstimateId = new Map(
    estimates.map((estimate) => [
      estimate.campaignsEstimatesId,
      new Set(
        (estimate.simulation?.operations ?? [])
          .filter((operation) => operation.id && (operation.actions?.length ?? 0) > 0)
          .map((operation) => operation.id),
      ),
    ]),
  );

  return (activeCampaign?.operationSequence ?? []).reduce<string[]>((keys, sequenceItem) => {
    const estimate = estimateById.get(sequenceItem.campaignsEstimatesId);
    const section = estimate?.sections?.find((item) => item.projectSectionId === sequenceItem.projectSectionId);
    const trackableOperationIds =
      trackableOperationIdsByEstimateId.get(sequenceItem.campaignsEstimatesId) ?? new Set<string>();
    const hasTrackableOperations = (section?.operationIds ?? []).some((operationId) =>
      trackableOperationIds.has(operationId),
    );

    if (hasTrackableOperations) {
      keys.push(getOperationSequenceKey(sequenceItem));
    }

    return keys;
  }, []);
};

const getCurrentTimeTrackerSequenceKeys = (operationRows: TCampaignTimeTrackerOperationRow[]) =>
  operationRows
    .map(({ estimate, operation }) => ({
      campaignsEstimatesId: estimate.campaignsEstimatesId,
      projectSectionId: operation.section?.projectSectionId,
      start: operation.start,
    }))
    .filter(
      (
        item,
      ): item is {
        campaignsEstimatesId: string;
        projectSectionId: string;
        start: string | Date | undefined;
      } => !!item.projectSectionId,
    )
    .sort((a, b) => getStartTime(a.start) - getStartTime(b.start))
    .reduce<string[]>((keys, item) => {
      const key = getOperationSequenceKey(item);
      if (keys[keys.length - 1] !== key) {
        keys.push(key);
      }
      return keys;
    }, []);

export const isTimeTrackerSequenceChanged = (
  activeCampaign: IActiveCampaign | undefined,
  operationRows: TCampaignTimeTrackerOperationRow[],
) => {
  const currentKeys = getCurrentTimeTrackerSequenceKeys(operationRows);
  const expectedKeys = getExpectedTimeTrackerSequenceKeys(activeCampaign);

  return (
    expectedKeys.length > 0 &&
    (currentKeys.length !== expectedKeys.length || expectedKeys.some((key, index) => key !== currentKeys[index]))
  );
};
